import Tag from '@Components/Common/Tag';
import { DashboardRounded } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Card,
    CardHeader,
    CardMedia,
    SxProps,
} from '@mui/material';
import { maxToneToLevel } from '@utils/colorUtils';

interface TemplateCardProps {
    template: any;
    size?: 'medium' | 'large';
    onClick?: (template: any, e: React.MouseEvent) => void;
    onSelect?: (template: any, e: React.MouseEvent) => void;
    cardActions?: React.ReactNode;
    selected?: boolean;
    sxProps?: SxProps;
}

const sizeStyles = {
    medium: { height: 196, avatarFontSize: 150 },
    large: { height: 260, avatarFontSize: 200 },
};

const TemplateCard = ({
    size = 'medium',
    template,
    onClick,
    onSelect,
    cardActions,
    selected = false,
    sxProps,
}: TemplateCardProps) => {
    const styles = sizeStyles[size];

    return (
        <Card
            variant="elevation"
            onClick={e => onClick?.(template, e)}
            onDoubleClick={e => onSelect?.(template, e)}
            data-active={selected ? '' : undefined}
            sx={{
                ...sxProps,
                cursor: 'pointer',
                '&[data-active]': {
                    backgroundColor: 'secondaryContainer.main',
                    '&:hover': {
                        backgroundColor: 'action.selectedHover',
                    },
                },
            }}
        >
            <CardMedia
                sx={{
                    position: 'relative',
                    height: styles.height,
                    gap: 0.5,
                    display: 'grid',
                    ...(template.variants_count === 1 && {
                        gridTemplateRows: '1fr',
                        gridTemplateColumns: '1fr',
                        gridTemplateAreas: `"a"`,
                    }),
                    ...(template.variants_count === 2 && {
                        gridTemplateRows: '1fr 1fr',
                        gridTemplateColumns: '1fr',
                        gridTemplateAreas: `"a" "b"`,
                    }),
                    ...(template.variants_count >= 3 && {
                        gridTemplateRows: '2fr 1fr',
                        gridTemplateColumns: '1fr 1fr',
                        gridTemplateAreas: `
                            "a a"
                            "b c"
                        `,
                    }),
                }}
            >
                {template.variants_thumbnails.map((thumb, i) => (
                    <Avatar
                        key={i}
                        variant="square"
                        sx={{
                            bgcolor: maxToneToLevel(
                                template.background_color,
                                94,
                            ),
                            width: '100%',
                            height: '100%',
                            borderRadius: 2,
                            objectFit: 'cover',
                            gridArea: ['a', 'b', 'c'][i],
                        }}
                        src={thumb}
                    >
                        <DashboardRounded
                            sx={{
                                fontSize:
                                    styles.avatarFontSize /
                                    [[1], [2, 2], [1.5, 3, 3]][
                                        Math.min(template.variants_count, 3) - 1
                                    ][i],
                            }}
                        />
                    </Avatar>
                ))}
                {template.variants_count > 3 && (
                    <Tag
                        sx={{
                            position: 'absolute',
                            bottom: 6,
                            right: 6,
                        }}
                        size="small"
                        label={`+${template.variants_count - 3}`}
                    />
                )}
            </CardMedia>
            <CardHeader
                title={template.parent_title}
                titleTypographyProps={{
                    noWrap: true,
                    variant: 'titleLarge',
                }}
                sx={{
                    '& .MuiCardHeader-content': {
                        overflow: 'hidden',
                    },
                }}
                action={
                    <Box onClick={e => e.stopPropagation()}>{cardActions}</Box>
                }
            />
        </Card>
    );
};

export default TemplateCard;
